import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AddDryGoods() {
  const nav = useNavigate();
  const [prodName, setProdName] = useState("");
  const [businessPid, setBusinessPid] = useState("");
  const [category, setCategory] = useState("Dry Goods");
  const [packType, setPackType] = useState("CASE");
  const [unitSize, setUnitSize] = useState("");
  const [unitsPerPack, setUnitsPerPack] = useState(1);
  const [packsPerCase, setPacksPerCase] = useState(1);
  const [par, setPar] = useState(10);
  const [reorderPt, setReorderPt] = useState(5);
  const [vendor, setVendor] = useState("");
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!prodName.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      // Create product + variant in sequence. Internal ID is auto-generated
      // server-side (stable_id from name). The optional Product ID field is
      // the business/vendor/purchasing ID.
      const prod = await api.post("/items/products", {
        name: prodName.trim(),
        short_name: prodName.split(",")[0].trim(),
        business_product_id: businessPid.trim() || null,
        category,
      });
      const v = await api.post("/items/variants", {
        product_id: prod.data.product_id,
        pack_type: packType,
        unit_size: unitSize,
        units_per_pack: parseInt(unitsPerPack, 10) || 1,
        packs_per_case: parseInt(packsPerCase, 10) || 1,
        base_unit: packType,
        par_level: Number(par),
        reorder_point: Number(reorderPt),
        vendor,
      });
      toast.success(`Created ${v.data.product_name}`);
      nav("/items");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary">← Admin</Link>
      <div className="my-3">
        <div className="label-tech mb-1">Configuration</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Add New Dry-Goods Item</h1>
      </div>
      <form onSubmit={submit} className="card-flat border-border p-4 space-y-3">
        <label className="text-xs block">
          <div className="label-tech mb-1">Item Name</div>
          <input value={prodName} onChange={(e) => setProdName(e.target.value)} required className="w-full h-10 px-3 border border-border bg-background rounded-sm text-sm" placeholder="e.g., Rice, Basmati, 25 lb bag" />
        </label>
        <label className="text-xs block">
          <div className="label-tech mb-1">Product ID <span className="text-muted-foreground normal-case ml-1">(optional — vendor/purchasing ID)</span></div>
          <input data-testid="add-business-product-id" value={businessPid} onChange={(e) => setBusinessPid(e.target.value)}
            placeholder="e.g., FRANZR (leave blank if unknown)"
            className="w-full h-10 px-3 border border-border bg-background rounded-sm font-mono text-sm" />
          <div className="text-[10px] text-muted-foreground mt-1">Internal ID is auto-generated and does not need to be entered.</div>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <div className="label-tech mb-1">Category</div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm">
              {cats.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
            </select>
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Pack Type</div>
            <select value={packType} onChange={(e) => setPackType(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm">
              {["CASE", "BOX", "BAG", "BOTTLE", "CAN", "TUB", "PACK", "EACH"].map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs">
            <div className="label-tech mb-1">Unit Size</div>
            <input value={unitSize} onChange={(e) => setUnitSize(e.target.value)} placeholder="e.g., Pounds" className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Units/Pack</div>
            <input type="number" min="1" value={unitsPerPack} onChange={(e) => setUnitsPerPack(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Packs/Case</div>
            <input type="number" min="1" value={packsPerCase} onChange={(e) => setPacksPerCase(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <div className="label-tech mb-1">PAR Level</div>
            <input type="number" min="0" value={par} onChange={(e) => setPar(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
          <label className="text-xs">
            <div className="label-tech mb-1">Reorder Point</div>
            <input type="number" min="0" value={reorderPt} onChange={(e) => setReorderPt(e.target.value)} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
          </label>
        </div>
        <label className="text-xs block">
          <div className="label-tech mb-1">Vendor (optional)</div>
          <input value={vendor} onChange={(e) => setVendor(e.target.value)} className="w-full h-10 px-3 border border-border bg-background rounded-sm text-sm" />
        </label>
        <button type="submit" disabled={saving} className="btn-hard w-full h-11 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-60">
          {saving ? "Saving…" : "Create Item"}
        </button>
      </form>
    </div>
  );
}
